/**
 * Client Profile — trust, company, and partnership structure types (plain-language labels).
 * Ported from frontend/src/constants/clientStructureTypes.js — deliberately omits the UI-only
 * form-building/validation exports (`createStructureId`, `createEmptyStructure`,
 * `validateStructureApportionment`) since the calculation engine never calls them.
 */

export const ENTITY_OWNERSHIP_PREFIX = 'entity:';

export const CLIENT_STRUCTURE_TYPES = [
    { id: 'family_trust', label: 'Family trust' },
    { id: 'unit_trust', label: 'Unit trust' },
    { id: 'private_company', label: 'Private company' },
    { id: 'partnership', label: 'Partnership' },
    { id: 'other', label: "Other / I'm not sure" },
];

export interface ClientStructure {
    id?: string;
    type?: string;
    apportionmentClient?: number;
    apportionmentPartner?: number;
    [key: string]: unknown;
}

export function entityOwnershipValue(entityId: string): string {
    return `${ENTITY_OWNERSHIP_PREFIX}${entityId}`;
}

export function isEntityOwnership(ownership: unknown): ownership is string {
    return typeof ownership === 'string' && ownership.startsWith(ENTITY_OWNERSHIP_PREFIX);
}

export function getEntityIdFromOwnership(ownership: unknown): string | null {
    if (!isEntityOwnership(ownership)) return null;
    return ownership.slice(ENTITY_OWNERSHIP_PREFIX.length);
}

export function getStructureTypeLabel(typeId: string): string {
    return CLIENT_STRUCTURE_TYPES.find((t) => t.id === typeId)?.label || 'Arrangement';
}

/** Combined Centrelink apportionment % for applicants on this structure (capped 0–100). */
export function getApplicantsCombinedApportionmentPercent(structure: ClientStructure, isPartnered: boolean): number {
    const clientPct = Math.min(100, Math.max(0, structure.apportionmentClient || 0));
    const partnerPct = isPartnered ? Math.min(100, Math.max(0, structure.apportionmentPartner || 0)) : 0;
    return Math.min(100, clientPct + partnerPct);
}

/** Remainder % treated as other family members — not shown in applicant net worth. */
export function getApportionmentRemainderPercent(structure: ClientStructure, isPartnered: boolean): number {
    return Math.max(0, 100 - getApplicantsCombinedApportionmentPercent(structure, isPartnered));
}
